import { observable, runInAction } from 'mobx'
import type {
  OperationCancellationReason,
  OperationDefinition,
  OperationExecutionContext,
  OperationGroup,
  OperationHandle,
  OperationKey,
  OperationOwnerLifetime,
  OperationPublication,
  OperationResult,
  OperationStatus,
  ReadonlyOperationState,
} from './operation.types'
import { assertValidCallbackOutput } from './operation.helpers'
import { Lane, type ResolvedExecutionPolicy } from './operation.lane'
import type { OperationDebugHandleSnapshot } from './operation.debug.types'

export interface OperationTimelineEntryInput {
  readonly operationId: string
  readonly ownerId: string
  readonly key: string
  readonly status: OperationStatus
  readonly errorSummary: string | null
}

const UNLOADED_STATE: ReadonlyOperationState<never> = Object.freeze({ status: 'unloaded' as const, error: null })

/** Best-effort, diagnostic-only — never a domain PM, so a defensive stringify is fine here. */
function summarizeDebugError(error: unknown): string {
  try {
    return JSON.stringify(error) ?? String(error)
  } catch {
    return String(error)
  }
}

export interface OperationHandleDependencies {
  readonly generateUnitId: () => string
  readonly log: (message: string, context: Record<string, unknown>) => void
  /** Timeout deadlines and retry backoff timers (spec §12) — never a bare setTimeout. */
  readonly after: (delayMs: number, callback: () => void) => () => void
  /** Forwarded to OperationRepository's global activity ledger for isBusy/isGroupBusy/pendingCount. */
  readonly onGlobalUnitStart: (unitId: string, groups: readonly OperationGroup[]) => void
  readonly onGlobalUnitEnd: (unitId: string) => void
  /** Forwarded to OperationRepository's debug timeline ring buffer — see operation.debug.types.ts. */
  readonly onTimelineEntry: (entry: OperationTimelineEntryInput) => void
}

/**
 * Owner-bound handle (spec §9.2): owns the observable state map used by
 * `stateFor` (reads never mutate on an unseen key) and the private lane map
 * used for execution mechanics. One instance per `owner.register(definition)`
 * call — even if several owners share the same frozen definition object,
 * each gets independent state.
 */
export class OperationHandleImpl<Args, Failure> implements OperationHandle<Args, Failure> {
  private readonly stateMap = observable.map<OperationKey, ReadonlyOperationState<Failure>>(undefined, { deep: false })
  private readonly lanes = new Map<OperationKey, Lane<unknown, Failure>>()
  /** Local activity ledger — unitId -> groups, mirrors what's forwarded globally, scoped to this handle only. */
  private readonly activityUnits = observable.map<string, readonly OperationGroup[]>(undefined, { deep: false })
  private disposed = false
  private readonly definition: OperationDefinition<Args, Failure>
  private readonly ownerId: string
  private readonly lifetime: OperationOwnerLifetime
  private readonly deps: OperationHandleDependencies

  constructor(
    definition: OperationDefinition<Args, Failure>,
    ownerId: string,
    lifetime: OperationOwnerLifetime,
    deps: OperationHandleDependencies,
  ) {
    this.definition = definition
    this.ownerId = ownerId
    this.lifetime = lifetime
    this.deps = deps
  }

  get isBusy(): boolean {
    return this.activityUnits.size > 0
  }

  get pendingCount(): number {
    return this.activityUnits.size
  }

  stateFor(args: Args): ReadonlyOperationState<Failure> {
    const key = this.resolveKey(args)
    return this.stateMap.get(key) ?? (UNLOADED_STATE as ReadonlyOperationState<Failure>)
  }

  run<Value>(
    args: Args,
    execute: (context: OperationExecutionContext) => Promise<OperationResult<Value, Failure>>,
    publication: OperationPublication<Value, Failure>,
  ): Promise<OperationResult<Value, Failure>> {
    if (this.disposed) {
      throw new Error(`${this.definition.id}: cannot run() on a disposed owner (${this.ownerId}).`)
    }

    const key = this.resolveKey(args)
    const groups = this.resolveGroups(args)
    const policy = this.resolvePolicy(args)
    const lane = this.laneFor(key)

    return lane.run(this.definition.pending, execute as never, publication as never, groups, policy) as Promise<
      OperationResult<Value, Failure>
    >
  }

  cancel(args: Args, reason: OperationCancellationReason): void {
    const key = this.resolveKey(args)
    this.lanes.get(key)?.cancel(reason)
  }

  reset(args: Args, reason: OperationCancellationReason): void {
    const key = this.resolveKey(args)
    this.lanes.get(key)?.resetToUnloaded(reason)
  }

  resetAll(reason: OperationCancellationReason): void {
    for (const lane of this.lanes.values()) lane.resetToUnloaded(reason)
  }

  /** Owner-level cancellation (dispose/session-reset) — active + queued, restores last stable state per key. */
  cancelAllInternal(reason: OperationCancellationReason): void {
    for (const lane of this.lanes.values()) lane.cancel(reason)
  }

  cancelGroupInternal(group: OperationGroup, reason: OperationCancellationReason): void {
    for (const lane of this.lanes.values()) {
      if (lane.isPending && lane.groups.includes(group)) lane.cancel(reason)
    }
  }

  resetGroupInternal(group: OperationGroup, reason: OperationCancellationReason): void {
    for (const lane of this.lanes.values()) {
      if (lane.groups.includes(group)) lane.resetToUnloaded(reason)
    }
  }

  disposeHandle(reason: OperationCancellationReason): void {
    this.cancelAllInternal(reason)
    this.disposed = true
  }

  private laneFor(key: OperationKey): Lane<unknown, Failure> {
    let lane = this.lanes.get(key)
    if (!lane) {
      lane = new Lane<unknown, Failure>(
        { operationId: this.definition.id, ownerId: this.ownerId },
        {
          onStateChange: (state) => {
            runInAction(() => {
              this.stateMap.set(key, state)
              this.deps.onTimelineEntry({
                operationId: this.definition.id,
                ownerId: this.ownerId,
                key,
                status: state.status,
                errorSummary: state.status === 'errored' ? summarizeDebugError(state.error) : null,
              })
            })
          },
          onUnitStart: (unitId, groups) => {
            runInAction(() => {
              this.activityUnits.set(unitId, groups)
              this.deps.onGlobalUnitStart(unitId, groups)
            })
          },
          onUnitEnd: (unitId) => {
            runInAction(() => {
              this.activityUnits.delete(unitId)
              this.deps.onGlobalUnitEnd(unitId)
            })
          },
          getPublishedState: () => this.stateMap.get(key) ?? (UNLOADED_STATE as ReadonlyOperationState<Failure>),
          generateUnitId: this.deps.generateUnitId,
          log: this.deps.log,
          after: this.deps.after,
        },
      )
      this.lanes.set(key, lane)
    }
    return lane
  }

  /**
   * Resolved once per logical `run()` call, from `args` — Lane never sees
   * `Args`, only the plain values/closures below (spec §9.2, §12). The
   * timeout failure builder closes over `args` here so every attempt/retry
   * can produce it without threading `Args` through Lane's generic surface.
   */
  private resolvePolicy(args: Args): ResolvedExecutionPolicy<Failure> {
    return {
      timeout: this.resolveTimeout(args),
      retry: this.definition.retry,
      debounce: this.definition.debounce,
      idempotencyKey: this.resolveIdempotencyKey(args),
    }
  }

  private resolveTimeout(args: Args): ResolvedExecutionPolicy<Failure>['timeout'] {
    const timeout = this.definition.timeout
    if (timeout.kind === 'none') return { kind: 'none' }
    const toFailure = timeout.toFailure
    return { kind: 'after', milliseconds: timeout.milliseconds, buildFailure: () => toFailure(args) }
  }

  private resolveIdempotencyKey(args: Args): string | null {
    if (this.definition.safety.kind !== 'idempotency-keyed-write') return null
    const key = this.definition.safety.idempotencyKey(args)
    if (key !== key.trim() || key.length === 0) {
      throw new Error(`${this.definition.id}: idempotencyKey() produced an empty/untrimmed key.`)
    }
    return key
  }

  debugSnapshot(): OperationDebugHandleSnapshot {
    const keys = [...this.stateMap.entries()].map(([key, state]) => ({
      key,
      status: state.status,
      errorSummary: state.status === 'errored' ? summarizeDebugError(state.error) : null,
      groups: [...(this.lanes.get(key)?.groups ?? [])],
    }))

    return {
      operationId: this.definition.id,
      ownerId: this.ownerId,
      lifetime: this.lifetime,
      isBusy: this.isBusy,
      pendingCount: this.pendingCount,
      keys,
    }
  }

  private resolveKey(args: Args): OperationKey {
    const key = this.definition.key(args)
    assertValidCallbackOutput('OperationKey', key)
    return key
  }

  private resolveGroups(args: Args): readonly OperationGroup[] {
    const groups = Object.freeze([...this.definition.groups(args)])
    const seen = new Set<string>()
    for (const group of groups) {
      assertValidCallbackOutput('OperationGroup', group)
      if (seen.has(group)) {
        throw new Error(`${this.definition.id}: duplicate group "${group}" for one execution.`)
      }
      seen.add(group)
    }
    return groups
  }
}
